/**
 * BIAN landscape view (P6.4).
 *
 * The headline surface from the v1-vs-v2 audit: customer + SAMA
 * "speechless" feature. Renders the 19-zone / 75-section /
 * 544-tile BIAN catalogue as a tile grid with:
 *   - Overlay toggle (confidence / mvb / cascade / both)
 *   - Confidence-min filter
 *   - AsOfPicker integration for cascade time-travel
 *   - Per-tile click -> TileDetailDrawer
 *
 * Routes:
 *   /twin/landscape/BIAN
 *
 * Backend: GET /v1/landscape/{catalog_code} -- twin-gated.
 */
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { AsOfPicker } from "shared/components/AsOfPicker";
import { useAsOf } from "shared/hooks/useAsOf";
import { useLandscape } from "twin/api/hooks";
import type {
  LandscapeConfidence,
  LandscapeOverlay,
  LandscapePillar,
  LandscapeTileRollup,
} from "shared/api/types";

import { TileDetailDrawer } from "./TileDetailDrawer";

// -- Visual tone tables (v1 palette preserved) ----------------------

const CONFIDENCE_TONE: Record<LandscapeConfidence | "_empty", string> = {
  high: "bg-emerald-50 border-emerald-300 text-emerald-900",
  medium: "bg-sky-50 border-sky-300 text-sky-900",
  low: "bg-amber-50 border-amber-300 text-amber-900",
  unverified: "bg-slate-50 border-slate-300 text-slate-700",
  _empty: "bg-slate-50 border-slate-200 text-slate-400",
};

const PILLAR_TONE: Record<LandscapePillar, string> = {
  red: "bg-rose-100 border-rose-400 text-rose-900",
  amber: "bg-amber-100 border-amber-400 text-amber-900",
  green: "bg-emerald-100 border-emerald-400 text-emerald-900",
  unknown: "bg-slate-50 border-slate-200 text-slate-500",
};

const OVERLAY_LABELS: Record<LandscapeOverlay, string> = {
  confidence: "Confidence",
  mvb: "MVB",
  cascade: "Cascade",
  both: "Both",
};

const CONFIDENCE_MIN_LABELS: Record<LandscapeConfidence | "all", string> = {
  all: "All",
  unverified: "All",
  low: "Low+",
  medium: "Medium+",
  high: "High only",
};

// -- Component -----------------------------------------------------

export function BianLandscapeView() {
  const { t } = useTranslation();
  const params = useParams<{ catalogCode?: string }>();
  const catalogCode = params.catalogCode ?? "BIAN";

  const as_of = useAsOf() ?? undefined;
  const [overlay, setOverlay] = useState<LandscapeOverlay>("confidence");
  const [confidenceMin, setConfidenceMin] = useState<LandscapeConfidence | "all">(
    "all",
  );
  const [drawerTileId, setDrawerTileId] = useState<string | null>(null);

  const query = useLandscape(catalogCode, {
    as_of,
    overlay,
    confidence_min: confidenceMin === "all" ? undefined : confidenceMin,
  });

  const totalTiles = useMemo(() => {
    if (!query.data) return 0;
    return query.data.zones.reduce(
      (sum, z) => sum + z.sections.reduce((s, sec) => s + sec.tiles.length, 0),
      0,
    );
  }, [query.data]);

  return (
    <div data-testid="bian-landscape-view" className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-slate-900">
          {query.data?.name ?? t("screens.bianLandscapeView.bianLandscape")}
        </h1>
        <p className="text-sm text-slate-600">
          {query.data?.description ??
            t("screens.bianLandscapeView.bianAlignedBankingLandscape")}{" "}
          {totalTiles > 0 && (
            <span className="text-slate-500">
              ({query.data?.zones.length} {t("screens.bianLandscapeView.zones")}{" "}
              {totalTiles} {t("screens.bianLandscapeView.tiles")}
            </span>
          )}
        </p>
        <Link
          to={`/twin/landscape/${catalogCode}/classic`}
          data-testid="overlay-sister-link"
          className="inline-block text-xs text-brand-brassDark underline hover:text-brand-brass"
        >
          {t("screens.bianLandscapeView.switchToClassicViewV1StyleTile")}
        </Link>
      </header>

      <Toolbar
        overlay={overlay}
        onOverlayChange={setOverlay}
        confidenceMin={confidenceMin}
        onConfidenceMinChange={setConfidenceMin}
      />

      {query.isLoading && (
        <div className="rounded-md bg-slate-50 p-6 text-center text-slate-600">
          {t("screens.bianLandscapeView.loadingLandscape")}
        </div>
      )}
      {query.error && (
        <div
          data-testid="landscape-error"
          className="rounded-md bg-rose-50 border border-rose-200 p-4 text-rose-900"
        >
          {t("screens.bianLandscapeView.failedToLoadLandscape")} {query.error.message}
        </div>
      )}
      {query.data && (
        <div
          data-testid="landscape-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {query.data.zones.map((zone) => (
            <ZoneColumn
              key={zone.id}
              zone={zone}
              overlay={overlay}
              onTileClick={setDrawerTileId}
            />
          ))}
        </div>
      )}

      <TileDetailDrawer
        tileId={drawerTileId}
        as_of={as_of}
        onClose={() => setDrawerTileId(null)}
      />
    </div>
  );
}

// -- Toolbar -------------------------------------------------------

interface ToolbarProps {
  overlay: LandscapeOverlay;
  onOverlayChange: (next: LandscapeOverlay) => void;
  confidenceMin: LandscapeConfidence | "all";
  onConfidenceMinChange: (next: LandscapeConfidence | "all") => void;
}

function Toolbar({
  overlay,
  onOverlayChange,
  confidenceMin,
  onConfidenceMinChange,
}: ToolbarProps) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="landscape-toolbar"
      className="flex flex-wrap items-center gap-4 rounded-md border border-slate-200 bg-white p-3"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500">
          {t("screens.bianLandscapeView.overlay")}
        </span>
        {(Object.keys(OVERLAY_LABELS) as LandscapeOverlay[]).map((o) => (
          <button
            key={o}
            type="button"
            data-testid={`overlay-pill-${o}`}
            data-active={overlay === o}
            onClick={() => onOverlayChange(o)}
            className={`px-2 py-1 rounded text-xs border ${
              overlay === o
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {OVERLAY_LABELS[o]}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500">
          {t("screens.bianLandscapeView.confidence")}
        </span>
        {(["all", "low", "medium", "high"] as const).map((c) => (
          <button
            key={c}
            type="button"
            data-testid={`confidence-pill-${c}`}
            data-active={confidenceMin === c}
            onClick={() => onConfidenceMinChange(c)}
            className={`px-2 py-1 rounded text-xs border ${
              confidenceMin === c
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {CONFIDENCE_MIN_LABELS[c]}
          </button>
        ))}
      </div>
      <div className="ms-auto">
        <AsOfPicker />
      </div>
    </div>
  );
}

// -- Zone column ---------------------------------------------------

interface ZoneColumnProps {
  zone: {
    id: string;
    code: string;
    name: string;
    color_hex: string | null;
    sections: {
      id: string;
      name: string;
      tiles: LandscapeTileRollup[];
    }[];
  };
  overlay: LandscapeOverlay;
  onTileClick: (tileId: string) => void;
}

function ZoneColumn({ zone, overlay, onTileClick }: ZoneColumnProps) {
  return (
    <section
      data-testid="landscape-zone-column"
      data-zone-code={zone.code}
      className="rounded-md border border-slate-200 bg-white"
    >
      <header
        className="rounded-t-md p-2 text-xs font-semibold uppercase tracking-wide text-slate-900"
        style={{
          backgroundColor: zone.color_hex ?? "#E5E7EB",
        }}
      >
        {zone.name}
      </header>
      <div className="space-y-3 p-2">
        {zone.sections.map((section) => (
          <div key={section.id} data-testid="landscape-section">
            <h3 className="text-xs font-medium text-slate-600 mb-1">{section.name}</h3>
            <div className="grid grid-cols-2 gap-1">
              {section.tiles.map((tile) => (
                <TileCard
                  key={tile.id}
                  tile={tile}
                  overlay={overlay}
                  onClick={() => onTileClick(tile.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// -- Tile card -----------------------------------------------------

interface TileCardProps {
  tile: LandscapeTileRollup;
  overlay: LandscapeOverlay;
  onClick: () => void;
}

function TileCard({ tile, overlay, onClick }: TileCardProps) {
  const { t } = useTranslation();
  const tone = pickTileTone(tile, overlay);
  return (
    <button
      type="button"
      data-testid="landscape-tile-card"
      data-tile-id={tile.id}
      data-overlay={overlay}
      data-mapped-count={tile.mapped_service_count}
      data-mvb-count={tile.mvb_linked_count}
      data-pillar={tile.worst_resilience_pillar ?? ""}
      data-confidence={tile.best_confidence ?? ""}
      onClick={onClick}
      className={`rounded border px-2 py-1 text-start text-[11px] leading-tight transition hover:shadow-sm ${tone}`}
    >
      <div className="font-medium truncate">{tile.name}</div>
      <div className="mt-0.5 flex items-center gap-1 text-[10px] opacity-80">
        <span>
          {tile.mapped_service_count} {t("screens.bianLandscapeView.svc")}
        </span>
        {/* Round 12 H5 -- a flex gap drew both spaces and the text read
            "1 svcMVB-1": the badge glued to the count beside it, and
            the catalogue string carrying a trailing separator with the
            badge's own number glued to that. Two separators decided in
            two places, neither of which was the text. */}
        {tile.mvb_linked_count > 0 && (
          <>
            {" \u00b7 "}
            <span
              data-testid="tile-mvb-badge"
              className="rounded bg-violet-200 px-1 text-violet-900"
            >
              {t("screens.bianLandscapeView.mvb")}
              {" \u00b7 "}
              {tile.mvb_linked_count}
            </span>
          </>
        )}
      </div>
    </button>
  );
}

function pickTileTone(tile: LandscapeTileRollup, overlay: LandscapeOverlay): string {
  // No mapped services + no overlay state -> empty tone
  if (tile.mapped_service_count === 0) {
    return CONFIDENCE_TONE._empty;
  }

  if (overlay === "cascade" || overlay === "both") {
    if (tile.worst_resilience_pillar) {
      return PILLAR_TONE[tile.worst_resilience_pillar];
    }
    // No chain state at as_of -> fall through to neutral
    if (overlay === "cascade") {
      return PILLAR_TONE.unknown;
    }
  }

  if (overlay === "mvb") {
    if (tile.mvb_linked_count > 0) {
      return "bg-violet-50 border-violet-300 text-violet-900";
    }
    return CONFIDENCE_TONE._empty;
  }

  // confidence / both fallback
  if (tile.best_confidence) {
    return CONFIDENCE_TONE[tile.best_confidence];
  }
  return CONFIDENCE_TONE._empty;
}
